import { BaseService } from '@/modules/core/app/services/abstracts/BaseService';
import ClaraResource from '@/modules/ai/resources/ClaraResource';
import ClaraAsyncResource from '@/modules/ai/resources/ClaraAsyncResource';
import { AskClaraQuestionResponseObject } from '@/modules/ai/models/AskClaraQuestionResponseObject';
import { ClaraConversationObject } from '@/modules/ai/models/ClaraConversationObject';
import { ClaraConversationOrigin } from '@/modules/ai/constants/ClaraConversationOrigin';
import { EstateRagFeedbackObject } from '@/modules/ai/models/EstateRagFeedbackObject';
import { ClaraMessageObject } from '@/modules/ai/models/ClaraMessageObject';
import { Message } from '@/modules/messenger/_modules/message/models/Message';
import { MessageClaraDraftDiscardReason } from '@/modules/messenger/_modules/message/constants/MessageClaraDraftDiscardReason';

export class ClaraService extends BaseService {
  resource: typeof ClaraResource;
  asyncResource: typeof ClaraAsyncResource;

  constructor() {
    super();

    this.resource = ClaraResource;
    this.asyncResource = ClaraAsyncResource;
  }

  async askQuestion(
    text: string,
    identifier: string,
    modelName: string,
    conversationId: number,
    origin: ClaraConversationOrigin = ClaraConversationOrigin.CE_APP
  ): Promise<AskClaraQuestionResponseObject> {
    return this.asyncResource.askQuestion({
      text,
      identifier,
      modelName,
      conversationId,
      origin
    });
  }

  submitMessageFeedback(feedback: EstateRagFeedbackObject): Promise<void> {
    return this.resource.submitMessageFeedback({ ...feedback });
  }

  submitConversationFeedback(feedback: EstateRagFeedbackObject): Promise<void> {
    return this.resource.submitConversationFeedback({ ...feedback });
  }

  addMessageToLibrary(identifier: string, isInLibrary: boolean): Promise<void> {
    return this.resource.addMessageToLibrary({ identifier, isInLibrary });
  }

  async getConversations(): Promise<ClaraConversationObject[]> {
    return ClaraConversationObject.mapModels(await this.resource.getConversations());
  }

  cancelQuestion(identifier: string): Promise<void> {
    return this.resource.cancelQuestion({ identifier });
  }

  async pinConversation(conversationId: number, isPinned: boolean): Promise<void> {
    return this.resource.pinConversation({ conversationId, isPinned });
  }

  async updateConversationTitle(conversationId: number, title: string): Promise<void> {
    return this.resource.updateConversationTitle({ conversationId, title });
  }

  async setHasViewedClaraSplashScreen(): Promise<void> {
    return this.resource.setHasViewedClaraSplashScreen();
  }

  async setHasViewedClaraForPlannersLauncher(): Promise<void> {
    return this.resource.setHasViewedClaraForPlannersLauncher();
  }

  async sendDraft(messageId: number, text: string): Promise<Message> {
    return Message.mapModel(await this.resource.sendDraft({ messageId, text }));
  }

  async discardDraft(
    messageId: number,
    discardReason: MessageClaraDraftDiscardReason,
    discardDescription: string
  ): Promise<void> {
    return this.resource.discardDraft({
      messageId,
      discardReason: discardReason.key,
      discardDescription
    });
  }

  async requestSupportTicket(
    conversation: ClaraConversationObject,
    answer: ClaraMessageObject
  ): Promise<void> {
    return this.resource.requestSupportTicket({
      question: conversation.latestQuestion?.text ?? '',
      conversationId: conversation.id,
      escalationReason: answer.escalation?.reason ?? null,
      messages:
        conversation.messages?.map((message: ClaraMessageObject) => ({
          actor: message.actor,
          text: message.text
        })) ?? []
    });
  }
}

export default new ClaraService();
